import React, { useCallback, useEffect, useRef, useState } from "react";
import { adminApi } from "../apiAdmin";
import { withQuietRetry } from "../utils/adminRetry";
import AdminLoadFailed from "./AdminLoadFailed";
import { formatMoneyParts } from "../utils/currency";
import type { AdminFanList, AdminFanRow, FanAppFilter } from "../types/admin";
import FanFace from "./admin-fans/FanFace";
import FanDrawer from "./admin-fans/FanDrawer";
import {
  PAGE_SIZE,
  appLabels,
  buildFansCsv,
  contactLine,
  fansUrl,
  fetchAllFans,
  shortDate,
  signInShareLine,
  whenText,
} from "./admin-fans/format";
import "./AdminFansTab.css";

// Fans tab: every fan account — who they are, whether they use the app, what
// they requested and spent (each currency on its own, never converted), and
// when they last requested; how fans sign in is one percentage line, not a
// column (founder 2026-10-09). Search runs on the server (name, email,
// @instagram); a row opens the fan's drawer, where a request can be refunded.
// All of its state lives here, like the Disputes tab. Server:
// lib/admin/fans.js, lib/admin/refunds.js.

const CHIPS: { id: FanAppFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "app", label: "On the app" },
  { id: "web", label: "Web only" },
];

// The list on screen, with the search and chip it answers (Load more and the
// CSV follow those, not a newer choice still loading).
type View = AdminFanList & { q: string; app: FanAppFilter };

export default function AdminFansTab() {
  const [typed, setTyped] = useState("");
  const [q, setQ] = useState("");
  const [app, setApp] = useState<FanAppFilter>("all");
  const [view, setView] = useState<View | null>(null);
  const [failed, setFailed] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // The list whose "Load more" failed: says so (with Retry) until it works or
  // a new search or chip replaces the list.
  const [loadMoreFailed, setLoadMoreFailed] = useState<View | null>(null);
  const [exporting, setExporting] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  // Answers to an older search or chip are dropped.
  const latest = useRef(0);

  // The search runs 300 ms after the typing stops.
  useEffect(() => {
    const t = setTimeout(() => setQ(typed.trim()), 300);
    return () => clearTimeout(t);
  }, [typed]);

  const load = useCallback(() => {
    const call = ++latest.current;
    setFailed(false);
    adminApi
      .get<AdminFanList>(fansUrl({ q, app, limit: PAGE_SIZE, skip: 0 }))
      .then((r) => call === latest.current && setView({ ...withDefaults(r.data), q, app }))
      .catch(() => call === latest.current && setFailed(true));
  }, [q, app]);

  useEffect(load, [load]);

  const loadMore = () => {
    if (!view) return;
    const base = view;
    setLoadingMore(true);
    setLoadMoreFailed(null);
    withQuietRetry(() => adminApi.get<AdminFanList>(fansUrl({ q: base.q, app: base.app, limit: PAGE_SIZE, skip: base.items.length })))
      .then((r) => {
        const page = withDefaults(r.data);
        setView((now) => (now === base ? { ...base, ...page, items: [...base.items, ...page.items] } : now));
      })
      .catch(() => setLoadMoreFailed(base))
      .finally(() => setLoadingMore(false));
  };

  const downloadCsv = async () => {
    if (!view) return;
    const { q: shownQ, app: shownApp } = view;
    setExporting(true);
    try {
      const { rows, total } = await fetchAllFans((skip, limit) =>
        adminApi.get<AdminFanList>(fansUrl({ q: shownQ, app: shownApp, limit, skip })).then((r) => r.data)
      );
      saveFile(`request-fans-${new Date().toISOString().slice(0, 10)}.csv`, buildFansCsv(rows));
      if (rows.length < total) alert(`The file has the newest ${rows.length.toLocaleString()} of ${total.toLocaleString()} fans.`);
    } catch {
      alert("Couldn't build the CSV. Try again.");
    } finally {
      setExporting(false);
    }
  };

  const onPhotoRemoved = useCallback((id: string) => {
    setView((now) => now && { ...now, items: now.items.map((f) => (f._id === id ? { ...f, avatarUrl: null } : f)) });
  }, []);
  const closeDrawer = useCallback(() => setOpenId(null), []);
  // A refund changes the fan's counts and spent: the list re-reads them.
  const onRefunded = useCallback(() => load(), [load]);
  const signInShare = view ? signInShareLine(view.summary.providers) : null;

  return (
    <>
      <section className="fade-in">
        <div className="adm-fans-top">
          <input
            className="adm-search adm-fans-search"
            placeholder="Search name, email, @instagram…"
            aria-label="Search fans"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
          />
          <button type="button" className="adm-btn-light" disabled={exporting || !view?.total} onClick={downloadCsv}>
            {exporting ? "Preparing…" : "Download CSV"}
          </button>
        </div>

        {failed ? (
          <div className="adm-center adm-p-30">
            <p className="muted-text">Couldn't load fans.</p>
            <button type="button" className="adm-btn-light" onClick={load}>
              Retry
            </button>
          </div>
        ) : !view ? (
          <p className="muted-text adm-center adm-p-30">Loading fans…</p>
        ) : (
          <>
            <div className="adm-fans-tiles">
              {[
                { label: "Fan accounts", value: view.summary.total.toLocaleString() },
                { label: "New this week", value: view.summary.newThisWeek ? `+${view.summary.newThisWeek.toLocaleString()}` : "0" },
                { label: "Requested in last 30 days", value: view.summary.active30d.toLocaleString() },
                { label: "Paid requests from signed-in fans", value: `${Math.round(view.summary.signedInShare30d * 100)}%` },
              ].map((t) => (
                <div key={t.label} className="kpi-card kpi-card-compact">
                  <div>
                    <div className="kpi-label">{t.label}</div>
                    <div className="kpi-value">{t.value}</div>
                  </div>
                </div>
              ))}
            </div>
            {signInShare && <p className="muted-text adm-fs-80 adm-fans-share">{signInShare}</p>}
            <div className="adm-chips">
              {CHIPS.map((c) => (
                <button key={c.id} type="button" className={app === c.id ? "on" : ""} onClick={() => setApp(c.id)}>
                  {c.label} ({view.counts[c.id].toLocaleString()})
                </button>
              ))}
            </div>
            <div className="adm-table-wrapper adm-fans-scroll">
              <table className="adm-table-light adm-fans-table">
                <thead>
                  <tr>
                    <th>Fan</th>
                    <th>App</th>
                    <th>Requests</th>
                    <th>Spent</th>
                    <th>Talent supported</th>
                    <th>Last request</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {view.items.map((f) => (
                    <FanRow key={f._id} fan={f} onOpen={() => setOpenId(f._id)} />
                  ))}
                  {view.items.length === 0 && (
                    <tr>
                      <td colSpan={7} className="adm-center adm-p-30 adm-grey">
                        {emptyText(view)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Outside the wrapper, so a phone's sideways table swipe never hides it. */}
            {view.items.length < view.total && loadMoreFailed === view && (
              <AdminLoadFailed what="more fans" onRetry={loadMore} />
            )}
            {view.items.length < view.total && loadMoreFailed !== view && (
              <button type="button" className="adm-btn-light adm-center-block" disabled={loadingMore} onClick={loadMore}>
                {loadingMore ? "Loading…" : `Load more (${view.items.length} of ${view.total.toLocaleString()})`}
              </button>
            )}
          </>
        )}
      </section>
      {openId && <FanDrawer fanId={openId} onClose={closeDrawer} onPhotoRemoved={onPhotoRemoved} onRefunded={onRefunded} />}
    </>
  );
}

// A partial answer (an older or failing server) renders as zeros, never a crash.
const NO_SUMMARY: AdminFanList["summary"] = { total: 0, newThisWeek: 0, active30d: 0, signedInShare30d: 0 };
function withDefaults(d: Partial<AdminFanList> | null | undefined): AdminFanList {
  return {
    items: Array.isArray(d?.items) ? d!.items : [],
    total: typeof d?.total === "number" ? d.total : 0,
    counts: { all: 0, app: 0, web: 0, ...(d?.counts || {}) },
    summary: { ...NO_SUMMARY, ...(d?.summary || {}) },
  };
}

function emptyText(view: View): string {
  if (view.summary.total === 0) return "No fan accounts yet.";
  if (view.q) return "No fans match your search.";
  return view.app === "app" ? "No fans on the app yet." : "No web-only fans.";
}

function FanRow({ fan, onOpen }: { fan: AdminFanRow; onOpen: () => void }) {
  const apps = appLabels(fan.appPlatforms);
  const spent = formatMoneyParts(fan.spentByCurrency);
  const contact = contactLine(fan);
  return (
    <tr
      className="hover-row adm-fans-row"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <td>
        <div className="dj-cell">
          <FanFace fan={fan} />
          <div className="adm-fans-who">
            <span className={fan.name ? "dj-link-text adm-fs-85" : "muted-text adm-fs-85"}>{fan.name || "No name yet"}</span>
            <span className="muted-text adm-fs-72 adm-fans-contact" title={contact}>
              {contact}
            </span>
          </div>
        </div>
      </td>
      <td className="adm-nowrap">
        {apps.length ? (
          apps.map((a) => (
            <span key={a} className="pill adm-pill-sm adm-fans-pill">
              {a}
            </span>
          ))
        ) : (
          <span className="pill adm-pill-sm adm-pill-grey">Web</span>
        )}
      </td>
      <td className="adm-nowrap">
        {fan.requests}
        {fan.requests > 0 && <span className="muted-text adm-fs-76"> · {fan.played} played</span>}
      </td>
      <td className="adm-semibold">
        {spent.length ? (
          // Extra currencies wrap under the first; an amount never splits.
          <div className="adm-fans-spent">
            <span className="adm-nowrap">{spent[0]}</span>
            {spent.slice(1).map((p) => (
              <React.Fragment key={p}>
                {" "}
                <span className="muted-text adm-fs-76 adm-nowrap">+ {p}</span>
              </React.Fragment>
            ))}
          </div>
        ) : (
          <span className="adm-grey">—</span>
        )}
      </td>
      <td>{fan.talentCount}</td>
      <td className="adm-nowrap">{fan.lastRequestAt ? whenText(fan.lastRequestAt) : <span className="adm-grey">Never</span>}</td>
      <td className="adm-nowrap">{shortDate(fan.createdAt)}</td>
    </tr>
  );
}

// Hands the browser a text file to save (the same anchor trick as the venue
// QR download). The BOM lets Excel read accented names as UTF-8.
function saveFile(name: string, text: string) {
  const url = URL.createObjectURL(new Blob(["﻿", text], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
